import type { ComponentProps, ComponentType, JSX, SVGProps } from 'react';

import { parseHtml } from '@change-corgi/core/react/html';
import { Icon } from '@change-corgi/design-system/components/icon';
import type { IconComponent } from '@change-corgi/design-system/icons';
import { iconWarning } from '@change-corgi/design-system/icons';
import { Box } from '@change-corgi/design-system/layout';
import { Text } from '@change-corgi/design-system/typography';

import type { HttpError } from 'src/shared/error';

import { useErrorText } from 'src/app/shared/hooks/error';

type Status = HttpError['code'] | 500;

type Props = Readonly<{
	status: Status;
	errorId?: string;
}>;

// icons copied from https://github.com/change/styleguide/tree/v0.3.12/templates/symbols
// TODO those symbols should be migrated to the design system
function IconSettings(props: SVGProps<SVGSVGElement>) {
	return (
		<svg width="1em" height="1em" viewBox="0 0 512 512" {...props}>
			<g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
				<g fill="currentColor">
					<path d="M511.992145,301.393763 L511.992145,210.576245 L456.266979,210.576245 C451.053434,187.501255 441.932407,165.895855 429.690823,146.484842 L469.113395,107.075838 L404.902739,42.8787503 L365.477311,82.2727587 C346.084151,70.0304603 324.448045,60.9094338 301.385194,55.6966025 L301.385194,0 L210.57696,0 L210.57696,55.6966025 C187.484831,60.9094338 165.910137,70.0304603 146.485556,82.2727587 L107.060842,42.8787503 L42.8501869,107.075838 L82.3034644,146.484842 C70.0011827,165.895141 60.9101478,187.500541 55.6987448,210.576245 L0,210.576245 L0,301.394477 L55.6980307,301.394477 C60.9094338,324.485891 70.0004686,346.092006 82.3027503,365.485166 L42.8494728,404.923448 L107.060128,469.119821 L146.484842,429.696536 C165.909423,441.968826 187.484831,451.060575 210.576245,456.271978 L210.576245,512 L301.385194,512 L301.385194,456.273406 C324.446616,451.062003 346.084151,441.970254 365.475883,429.697964 L404.902739,469.12125 L469.113395,404.924876 L429.690823,365.515872 C441.932407,346.091291 451.053434,324.485177 456.266979,301.393763 L511.992145,301.393763 L511.992145,301.393763 Z M256,347.953629 C205.18275,347.953629 164.030661,306.787972 164.030661,256 C164.030661,205.212028 205.18275,164.045657 256,164.045657 C306.75084,164.045657 347.930778,205.212742 347.930778,256 C347.930778,306.787972 306.75084,347.953629 256,347.953629 L256,347.953629 Z"></path>
				</g>
			</g>
		</svg>
	);
}
function IconQuestion(props: SVGProps<SVGSVGElement>) {
	return (
		<svg width="1em" height="1em" viewBox="0 0 512 512" {...props}>
			<g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
				<g fill="currentColor">
					<path d="M361.292331,31.8640649 C336.133037,10.2260357 301.724378,-8.52651283e-14 259.856688,-8.52651283e-14 C229.03353,-8.52651283e-14 202.275231,3.76094323 179.546563,11.5073121 C157.55765,19.0457757 137.935818,28.6950644 120.811612,40.4593225 L112.776524,45.9484347 L155.093179,127.202076 L165.271556,120.096 C170.360744,116.623085 176.281553,113.070738 183.034674,109.613019 C189.62893,106.236804 196.607224,103.18039 203.937091,100.587446 C211.011394,97.9793057 218.277026,95.9624179 225.415565,94.5706271 C232.457405,93.1940321 238.986043,92.5855122 245.068479,92.5855122 C265.137895,92.5053892 276.820648,97.0827576 281.654272,102.52421 C287.800254,109.277331 291.193737,117.983794 291.256592,131.010679 C291.191665,140.790513 288.984831,148.391831 284.727263,154.825842 C279.702311,162.318718 273.395393,169.693482 265.521932,176.928722 C257.326599,184.498268 248.427427,192.436656 238.826488,200.712803 C228.391856,209.769458 219.205347,220.412686 211.170259,232.480858 C202.91207,245.012501 196.670769,259.55965 192.125174,276.163057 C188.925091,287.624782 187.451796,300.553585 187.451796,314.669728 C187.451796,321.743341 187.90007,329.106363 188.604599,336.852041 L189.53292,346.741698 L199.551051,346.741698 L281.750281,346.741698 L281.750281,335.793865 C281.750281,318.98946 284.470317,305.963265 289.49665,296.614438 C294.969876,285.891087 301.660142,276.640341 309.470747,268.614923 C317.504453,260.311147 326.561799,252.403151 336.390674,245.058779 C347.306043,236.896599 357.386338,227.421371 366.415365,216.761566 C375.824975,205.720487 383.635581,192.435274 389.748408,177.182905 C396.246655,161.002215 399.222946,141.623515 399.222946,118.863074 C399.318265,83.3824254 386.546944,53.2941888 361.292331,31.8640649 Z M237.993486,391.809471 C220.549478,391.777698 204.897185,397.027132 193.341523,407.94043 C181.467441,418.855799 175.578405,434.411392 175.705497,451.599835 C175.577714,468.788278 181.466751,484.34387 193.277287,495.451949 C204.736939,506.527564 220.324305,511.999409 237.610139,511.999409 L237.897476,511.999409 C256.20695,512.064336 272.371753,506.783129 284.118053,495.419486 C295.898888,484.34387 301.724378,468.785515 301.62975,451.599835 C301.72645,434.411392 295.898888,418.855799 283.992342,407.94043 C272.211507,396.89935 256.20695,391.777698 237.993486,391.809471 Z"></path>
				</g>
			</g>
		</svg>
	);
}

/* eslint-disable @typescript-eslint/naming-convention */
const ICONS: Record<403 | 'petition_under_review', IconComponent> = {
	403: iconWarning,
	petition_under_review: iconWarning,
};
const ICONS_OLD: Record<Exclude<Status, 401 | keyof typeof ICONS>, ComponentType<SVGProps<SVGSVGElement>>> = {
	404: IconQuestion,
	500: IconSettings,
};
/* eslint-enable @typescript-eslint/naming-convention */

const ICON_SIZE = 64;
const ICON_WIDTH = 108;
const ICON_MARGIN_LEFT = (ICON_WIDTH - 64) / 2 - 10;
const ICON_MARGIN_RIGHT = (ICON_WIDTH - 64) / 2 + 10;

function ErrorIcon({ status }: { status: Status }): JSX.Element | null {
	if (status in ICONS) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return <Icon icon={ICONS[status as keyof typeof ICONS]} sx={{ fontSize: 'inherit' }} />;
	}
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const IconOld = ICONS_OLD[status as keyof typeof ICONS_OLD];
	return <IconOld />;
}

export function ErrorPageContainer({
	status,
	errorId,
	...rest
}: Props & ComponentProps<typeof Box>): JSX.Element | null {
	const i18n = useErrorText(status);

	// this is made to match the error pages from fe as best as possible
	// TODO these should probably be redesigned once FE=>corgi migration is complete
	return (
		<Box
			sx={{
				maxWidth: 684,
				width: '100vw',
			}}
			pt={[60, 135]}
			pb={[60, 90]}
			mx="auto"
		>
			<Box
				sx={{
					position: 'relative',
					backgroundColor: '#fbd1d0',
					color: 'typography-critical',
				}}
				p={44}
				{...rest}
			>
				<Text
					data-qa="error-page-header"
					as="div"
					fontWeight="normal"
					sx={{
						fontSize: 24,
						lineHeight: '30px',
					}}
					mt={0}
					mb={15}
					pl={[0, ICON_WIDTH]}
					pr={[0, 60]}
				>
					{parseHtmlSafe(i18n.header)}
				</Text>
				<Box mt={24} mb={36}>
					<Box sx={{ position: 'absolute', display: ['none', 'block'] }} pl={ICON_MARGIN_LEFT} pr={ICON_MARGIN_RIGHT}>
						<Text sx={{ fontSize: ICON_SIZE }}>
							<ErrorIcon status={status} />
						</Text>
					</Box>
					<Text
						data-qa="error-page-main"
						as="div"
						fontWeight="normal"
						sx={{
							fontSize: i18n.main.length > 100 ? 18 : 36,
							lineHeight: i18n.main.length > 100 ? '28px' : '41px',
							p: {
								margin: '8px 0',
							},
						}}
						pl={[0, ICON_WIDTH]}
						pr={[0, 60]}
					>
						{parseHtmlSafe(i18n.main)}
					</Text>
				</Box>
				<Text as="div" pl={[0, ICON_WIDTH]} pr={[0, 60]}>
					{parseHtmlSafe(i18n.footer)}
				</Text>
				{errorId && (
					<Box sx={{ position: 'absolute', bottom: 4, right: 8 }}>
						<Text size="micro">Error ID: {errorId}</Text>
					</Box>
				)}
			</Box>
		</Box>
	);
}

function parseHtmlSafe(html: string) {
	try {
		return parseHtml(html);
	} catch (e) {
		return '';
	}
}
